<template>
  <div
    class="app-domain-switcher"
    :class="{ 'app-domain-switcher--collapsed': collapsed }"
    data-tour="nav-domain-switcher"
  >
    <button
      v-if="collapsed"
      type="button"
      class="app-domain-switcher__icon-btn"
      :aria-label="collapsedLabel"
      @click.stop="onCollapsedClick"
    >
      <v-icon size="22">{{ collapsedIcon }}</v-icon>
    </button>

    <div
      v-else
      class="app-domain-switcher__tabs"
      role="radiogroup"
      aria-label="Dominio"
      @keydown="onGroupKeydown"
    >
      <button
        v-for="option in options"
        :key="option.id"
        type="button"
        role="radio"
        class="app-domain-switcher__tab"
        :class="{ 'app-domain-switcher__tab--active': selected === option.id }"
        :aria-checked="selected === option.id"
        :tabindex="selected === option.id ? 0 : -1"
        :data-tour="`nav-domain-${option.id}`"
        @click="onSelect(option.id)"
      >
        <v-icon size="18">{{ option.icon }}</v-icon>
        <span>{{ option.label }}</span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useAppDomainStore, type AppDomain } from '@/stores/appDomain'

type SwitchableDomain = Exclude<AppDomain, 'read'>

const DOMAIN_OPTIONS: Array<{
  id: SwitchableDomain
  label: string
  icon: string
}> = [
  { id: 'write', label: 'Escribir', icon: 'mdi-pencil-outline' },
  { id: 'community', label: 'Comunidad', icon: 'mdi-account-group-outline' },
]

export default defineComponent({
  name: 'AppDomainSwitcher',

  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const appDomainStore = useAppDomainStore()
    return { appDomainStore }
  },

  computed: {
    options() {
      return DOMAIN_OPTIONS
    },

    selected(): SwitchableDomain {
      return this.appDomainStore.selected === 'community' ? 'community' : 'write'
    },

    nextDomain(): SwitchableDomain {
      return this.selected === 'write' ? 'community' : 'write'
    },

    collapsedIcon(): string {
      const match = DOMAIN_OPTIONS.find((option) => option.id === this.selected)
      return match?.icon ?? 'mdi-pencil-outline'
    },

    collapsedLabel(): string {
      const next = DOMAIN_OPTIONS.find((option) => option.id === this.nextDomain)
      return `Cambiar a ${next?.label ?? 'Escribir'}`
    },
  },

  methods: {
    onSelect(domain: SwitchableDomain) {
      this.appDomainStore.selectDomain(domain, this.$router)
    },

    onCollapsedClick() {
      this.appDomainStore.toggleWriteCommunity(this.$router)
    },

    onGroupKeydown(event: KeyboardEvent) {
      const isHorizontal = event.key === 'ArrowLeft' || event.key === 'ArrowRight'
      const isVertical = event.key === 'ArrowUp' || event.key === 'ArrowDown'
      if (!isHorizontal && !isVertical) {
        return
      }
      event.preventDefault()
      const currentIndex = DOMAIN_OPTIONS.findIndex((option) => option.id === this.selected)
      const delta = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1
      const nextIndex = (currentIndex + delta + DOMAIN_OPTIONS.length) % DOMAIN_OPTIONS.length
      const next = DOMAIN_OPTIONS[nextIndex]
      if (!next) return
      this.onSelect(next.id)
      void this.$nextTick(() => {
        const active = this.$el.querySelector('[role="radio"][aria-checked="true"]')
        if (active instanceof HTMLElement) {
          active.focus()
        }
      })
    },
  },
})
</script>

<style scoped>
.app-domain-switcher {
  padding: 8px 12px;
}

.app-domain-switcher--collapsed {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}

.app-domain-switcher__icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: rgba(var(--v-theme-on-surface), 0.7);
  cursor: pointer;
}

.app-domain-switcher__icon-btn:hover {
  background: rgba(33, 150, 243, 0.08);
}

.app-domain-switcher__tabs {
  display: flex;
  overflow: hidden;
  border-radius: 10px;
  background: rgba(var(--v-theme-on-surface), 0.06);
}

.app-domain-switcher__tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 36px;
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: rgba(var(--v-theme-on-surface), 0.7);
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}

.app-domain-switcher__tab--active,
.app-domain-switcher__tab--active:hover {
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
}

.app-domain-switcher__tab:hover:not(.app-domain-switcher__tab--active) {
  background: rgba(var(--v-theme-on-surface), 0.06);
}
</style>
